import { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import config from '../../config';
import { encryptData } from '../../utils/cryptoUtils';

function BeatPlanTab({ leadId }) {
  const [beatPlans, setBeatPlans] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const userInfo = useSelector((state) => state.userInfo);
  const userId = userInfo?.id;

  const fetchBeatPlans = async () => {
    if (!leadId) return;

    try {
      setLoading(true);
      setError('');

      const response = await fetch(`${config.API_BASE_URL}masters/get_lead_beat_plan`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: encryptData({
          lead_id: leadId,
          user_id: userId,
        }),
      });

      const result = await response.json();

      if (result.success) {
        setBeatPlans(Array.isArray(result.data) ? result.data : []);
      } else {
        setBeatPlans([]);
        setError(result.message || 'No beat plan records found.');
      }
    } catch (e) {
      setBeatPlans([]);
      setError('Something went wrong while fetching beat plans.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchBeatPlans();

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [leadId]);

  const formatDateTime = (dt) => {
    if (!dt || dt === '0000-00-00 00:00:00') {
      return 'N/A';
    }

    const d = new Date(dt);

    if (Number.isNaN(d.getTime())) {
      return dt;
    }

    return d.toLocaleString('en-IN', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const formatDate = (dt) => {
    if (!dt || dt === '0000-00-00') {
      return 'N/A';
    }

    const d = new Date(dt);

    if (Number.isNaN(d.getTime())) {
      return dt;
    }

    return d.toLocaleDateString('en-IN', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
    });
  };

  return (
    <div>
      <h5 className="fw-bold mb-3">Beat Plan</h5>

      {error && <div className="text-danger mb-3">{error}</div>}

      <div className="table-wrap">
        <table className="table table-hover table-bordered align-middle">
          <thead className="table-light">
            <tr>
              <th>Date &amp; Time</th>
              <th>Agent</th>
              <th>Company / Contact</th>
              <th>Result</th>
              <th>Remark</th>
              <th>Next Follow Up Date</th>
            </tr>
          </thead>

          <tbody>
            {loading ? (
              <tr>
                <td colSpan={6} className="text-center py-5">
                  <div className="spinner-border text-primary" role="status">
                    <span className="visually-hidden">Loading...</span>
                  </div>
                </td>
              </tr>
            ) : beatPlans.length === 0 ? (
              <tr>
                <td colSpan={6} className="text-center py-4">
                  No beat plan records found.
                </td>
              </tr>
            ) : (
              beatPlans.map((row, index) => (
                <tr key={row.id || index}>
                  <td>{formatDateTime(row.visit_start_time || row.scheduled_time)}</td>

                  <td>{row.agentName || 'N/A'}</td>

                  <td>
                    {row.company_name || row.contact_name
                      ? `${row.company_name || ''} ${row.contact_name ? `(${row.contact_name})` : ''}`
                      : 'N/A'}
                  </td>

                  <td>{row.outcomeName || 'N/A'}</td>

                  <td>{row.visit_remark || row.remarks || 'N/A'}</td>

                  <td>{formatDate(row.next_follow_up_date)}</td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export default BeatPlanTab;
